import { isNewIdentifiant, request } from '@mairistem/tools';
import React from 'react';
import { ENV_LOCAL } from '@/constants';
import { baseUrl, deleteLogoRegie, uploadDocument } from '@/routes';

import { ImageUploader } from '../Autre';

interface RegieLogoProps {
  identifiant: string | number;
  logo?: string;
  titre?: string;
  onChange: (data: { identifiant: string | number; logo: string | null }) => void;
}

const RegieLogo = (props: RegieLogoProps) => {
  const { identifiant, logo, titre, onChange } = props;

  const [URLLogo, setURLLogo] = React.useState<string>(null);
  const [loadingLogo, setLoadingLogo] = React.useState(false);

  const loadLogoUrl = React.useCallback(() => {
    setLoadingLogo(true);
    // Si on arrive sur une fiche existante
    if (identifiant && !isNewIdentifiant(identifiant)) {
      if (ENV_LOCAL) {
        // En local on balance juste l'URL de l'API, on simule une attente
        setTimeout(() => {
          setURLLogo(`${baseUrl}/regies/${identifiant}/logo`);
          setLoadingLogo(false);
        }, 1000);
      } else {
        // Si en déployé, il faut récup l'url temporaire du S3
        setURLLogo(null);
        setLoadingLogo(true);

        request
          .get(`${baseUrl}/regies/${identifiant}/logo?temporaryUrl=true`)
          .then(({ url }: { url: string }) => {
            setURLLogo(url);
          })
          .finally(() => {
            setLoadingLogo(false);
          });
      }
    } else {
      setLoadingLogo(false);
      setURLLogo(null);
    }
  }, [identifiant]);

  const handleLogoChange = React.useCallback(
    (file) => {
      // Si on est en création de fiche
      if (isNewIdentifiant(identifiant)) {
        // Alors quoi qu'il arrive on écrase de logo dans le state local
        onChange({ identifiant, logo: file });
      } else {
        // Si on est en modification de fiche
        // Alors on post le nouveau logo en remplaçant l'ancien

        // Convertir la chaîne base64 en Blob
        const byteString = atob(file.split(',')[1]);
        const mimeString = file.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i += 1) {
          ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], { type: mimeString });

        const formData = new FormData();
        const extension = mimeString.split('/')[1];
        formData.append('file', blob, `logo.${extension}`);

        setLoadingLogo(true);
        setURLLogo(null);

        request
          .upload(
            uploadDocument({
              titre: `Logo de ${titre}`,
              nomTableLiee: 'regie',
              identifiantTableLiee: identifiant,
              categorie: 'logo',
              replaceExisting: true,
            }),
            formData,
          )
          .finally(loadLogoUrl);
      }
    },
    [identifiant, loadLogoUrl, onChange, titre],
  );

  const handleLogoDelete = React.useCallback(() => {
    // Si on est en création de fiche
    if (isNewIdentifiant(identifiant)) {
      // Alors quoi qu'il arrive on supprime de logo dans le state local
      onChange({
        identifiant,
        logo: null,
      });
    } else {
      // Si on est en modification de fiche
      // Alors on supprime le logo côté serveur
      setLoadingLogo(true);
      request.delete(deleteLogoRegie(identifiant as number)).then(() => {
        setURLLogo(null);
        setLoadingLogo(false);
      });
    }
  }, [identifiant, onChange]);

  React.useEffect(() => {
    loadLogoUrl();
  }, [loadLogoUrl]);

  return (
    <ImageUploader
      key={`image-uploader-${identifiant}`}
      maxDimensions={{ width: 512, height: 512 }}
      value={logo || URLLogo}
      loading={loadingLogo}
      onChange={handleLogoChange}
      onDelete={handleLogoDelete}
    />
  );
};

export default RegieLogo;
